import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Facebook } from 'lucide-react';
import { AnimatedWaveDivider } from './WaveDivider';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_spray-wash-preview/artifacts/ycpj6xhu_logo.jpg%20%281%29.png';
const EVERWASH_URL = 'https://app.everwash.com/#membership-plans/143';
const FACEBOOK_URL = 'https://www.facebook.com/p/Super-Spray-Car-Wash-II-100057611704242/';

export const Footer = () => {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#0F172A] text-white" role="contentinfo" aria-label="Site footer">
      <AnimatedWaveDivider variant="top" toColor="dark" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <img 
                src={LOGO_URL} 
                alt="Super Spray Car Wash II" 
                className="h-16 w-auto"
              />
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Tahlequah's original tunnel car wash. Full-service experience: hand wash first, then through the tunnel.
            </p>
            <a 
              href={FACEBOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
              data-testid="footer-facebook-link"
            >
              <Facebook className="w-5 h-5" />
              <span>Follow us on Facebook</span>
            </a>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-lg mb-6 text-white">{t('footer.quicklinks')}</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-link-home">
                  {t('nav.home')}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-link-about">
                  {t('nav.about')}
                </Link>
              </li>
              <li>
                <Link to="/memberships" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-link-memberships">
                  Memberships
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-link-services">
                  Services
                </Link>
              </li>
              <li>
                <Link to="/resources" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-link-resources">
                  Resources
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-link-contact">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-bold text-lg mb-6 text-white">{t('footer.contact')}</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-400">
                  1581 S Muskogee Ave<br />
                  Tahlequah, OK 74464
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-red-500 flex-shrink-0" />
                <a 
                  href="tel:9182079463" 
                  className="text-slate-400 hover:text-white transition-colors"
                  data-testid="footer-phone"
                >
                  918-207-9463
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-red-500 flex-shrink-0" />
                <a 
                  href="mailto:superspray2@icloud.com" 
                  className="text-slate-400 hover:text-white transition-colors"
                  data-testid="footer-email"
                >
                  superspray2@icloud.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-red-500 flex-shrink-0" />
                <span className="text-slate-400">Open Daily: 9 AM - 7 PM</span>
              </li>
            </ul>
          </div>

          {/* CTA */}
          <div>
            <h4 className="font-bold text-lg mb-6 text-white">Get Unlimited Washes</h4>
            <p className="text-slate-400 text-sm mb-6">
              Join EverWash and wash as often as you want for one low monthly price.
            </p>
            <a 
              href={EVERWASH_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-everwash-cta"
            >
              <Button className="w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold py-3 rounded-md btn-primary">
                Join EverWash
              </Button>
            </a>
            <a 
              href="https://maps.app.goo.gl/pzyefhoDAQCvaCp89"
              target="_blank"
              rel="noopener noreferrer"
              className="block mt-3"
              data-testid="footer-directions-btn"
            >
              <Button variant="outline" className="w-full border-slate-600 text-white hover:bg-slate-800 font-medium py-3 rounded-md">
                Get Directions
              </Button>
            </a>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            &copy; {currentYear} Super Spray Car Wash II. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-slate-600 text-xs">
            <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
            <span>|</span>
            <span>{t('footer.serving')}</span>
          </div>
        </div>
        <div className="text-center mt-6">
          <p className="text-slate-700 text-xs"><a href="https://builtby-ab.com" target="_blank" rel="noopener noreferrer" className="hover:text-slate-400 transition-colors">Built by AB</a></p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
